import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { onError } from '../../../utils/error.util';
import { useAppContext } from '../../../utils/content.util';
import { getContractingItem, getFileDownloadUrl } from '../../../services/contracting.service';
import { ContractingItem } from '../../../models/contracting.model';

async function viewDocument(fileKey?: string) {
  if (!fileKey) return;
  const win = window.open('', '_blank');
  try {
    const downloadUrl = await getFileDownloadUrl(fileKey);
    if (win) win.location.href = downloadUrl;
  } catch (e) {
    win?.close();
    onError(e);
  }
}

function fileNameFromKey(fileKey: string): string {
  return fileKey.split('/').pop() || fileKey;
}

async function downloadFile(fileKey: string) {
  const downloadUrl = await getFileDownloadUrl(fileKey);
  const response = await fetch(downloadUrl);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileNameFromKey(fileKey);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body1">{value || '—'}</Typography>
    </Grid>
  );
}

const yesNoLabel = (val?: string) => (val === 'yes' ? 'Yes' : val === 'no' ? 'No' : undefined);

const salesforceUrl = (opportunityId: string) =>
  `https://collectorcarleaders.lightning.force.com/lightning/r/Account/${opportunityId}/view`;

export default function ContractingViewContainer() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppContext();
  const { contractingId } = useParams();
  const [item, setItem] = useState<ContractingItem | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  useEffect(() => {
    async function onLoad() {
      if (!isAuthenticated || !contractingId) {
        setIsLoading(false);
        return;
      }
      try {
        const response = await getContractingItem(contractingId);
        setItem(response);
      } catch (e) {
        onError(e);
      }
      setIsLoading(false);
    }
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractingId, isAuthenticated]);

  if (isLoading) return <CircularProgressComponent />;

  const fileKeys = [item?.billOfSaleFileKey, item?.titleFrontFileKey, item?.titleBackFileKey].filter(
    (key): key is string => Boolean(key)
  );

  async function handleDownloadAll() {
    setIsDownloadingAll(true);
    try {
      for (const fileKey of fileKeys) {
        await downloadFile(fileKey);
      }
    } catch (e) {
      onError(e);
    }
    setIsDownloadingAll(false);
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h2">
          Contracting Record
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" onClick={() => navigate('/admin-contracting')}>
          Back
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Overview
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Field
              label="Created Date"
              value={item?.createdAt ? format(new Date(item.createdAt), 'MM-dd-yyyy p') : undefined}
            />
            <Field
              label="Status"
              value={item?.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : undefined}
            />
            <Field label="Dealer Name" value={item?.dealerName} />
            <Field label="Dealer ID" value={item?.dealerId} />
            <Field label="First Name" value={item?.firstName} />
            <Field label="Last Name" value={item?.lastName} />
            <Field label="Email" value={item?.email} />
            <Field
              label="Opportunity ID"
              value={
                item?.opportunityId ? (
                  <Link
                    href={salesforceUrl(item.opportunityId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {item.opportunityId}
                    <OpenInNewIcon fontSize="inherit" />
                  </Link>
                ) : undefined
              }
            />
            <Field label="Analyst Email" value={item?.analystEmail} />
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Deal Checklist
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Field label="Sales Tax Collected" value={yesNoLabel(item?.salesTaxCollected)} />
            <Field label="Registration Fees Collected" value={yesNoLabel(item?.registrationFeesCollected)} />
            <Field label="Title Transfer Completed" value={yesNoLabel(item?.titleTransferCompleted)} />
            <Field label="Registration Transfer Completed" value={yesNoLabel(item?.registrationTransferCompleted)} />
            {item?.disclosureAgreementName && (
              <Field label="Disclosure Agreement Name" value={item.disclosureAgreementName} />
            )}
          </Grid>
        </Box>

        {item?.dealNotes && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Deal Notes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {item.dealNotes}
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="h6" gutterBottom>
            Documents
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            {fileKeys.length > 0 && (
              <Link
                component="button"
                underline="hover"
                onClick={handleDownloadAll}
                sx={{ fontWeight: 600 }}
                disabled={isDownloadingAll}
              >
                {isDownloadingAll ? 'Downloading…' : 'Download All'}
              </Link>
            )}
            {item?.billOfSaleFileKey ? (
              <Link component="button" underline="hover" onClick={() => viewDocument(item.billOfSaleFileKey)}>
                Bill of Sale
              </Link>
            ) : (
              <Typography color="text.secondary">Bill of Sale — not uploaded</Typography>
            )}
            {item?.titleFrontFileKey ? (
              <Link component="button" underline="hover" onClick={() => viewDocument(item.titleFrontFileKey)}>
                Title Front
              </Link>
            ) : (
              <Typography color="text.secondary">Title Front — not uploaded</Typography>
            )}
            {item?.titleBackFileKey ? (
              <Link component="button" underline="hover" onClick={() => viewDocument(item.titleBackFileKey)}>
                Title Back
              </Link>
            ) : (
              <Typography color="text.secondary">Title Back — not uploaded</Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
